<template>
  <div 
    v-if="visible" 
    class="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-end sm:items-center sm:justify-center lg:items-center lg:justify-center pb-[50px] sm:pb-0"
    @click="handleBackdropClick"
  >
    <!-- Mobile: Bottom Sheet Style | Desktop: Centered Modal -->
    <div 
      class="w-full bg-white rounded-t-2xl sm:rounded-lg lg:rounded-xl sm:max-w-sm lg:max-w-md sm:mx-4 animate-slide-up sm:animate-scale-in shadow-2xl sm:transform sm:translate-y-[-50px]"
      @click.stop
    >
      <!-- Header -->
      <div class="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg lg:text-xl font-semibold text-gray-900">{{ title || 'Enter Amount' }}</h3>
            <p v-if="subtitle" class="text-sm lg:text-base text-gray-500">{{ subtitle }}</p>
          </div>
          <button 
            @click="close"
            class="p-1 lg:p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6 lg:w-7 lg:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Display Area -->
      <div class="px-4 lg:px-6 py-6 lg:py-8 bg-gray-50">
        <div class="text-center">
          <div class="text-3xl lg:text-4xl font-bold text-gray-900 mb-2 min-h-[2.5rem] lg:min-h-[3rem] flex items-center justify-center">
            {{ displayValue || '0' }}
          </div>
          <div v-if="mode === 'currency'" class="text-sm lg:text-base text-gray-500">
            {{ formatCurrency(displayValue || '0') }}
          </div>
        </div>
      </div>

      <!-- Keypad Grid -->
      <div class="px-4 lg:px-6 pb-4 lg:pb-6">
        <div class="grid grid-cols-3 gap-3 lg:gap-4 mb-4 lg:mb-6">
          <!-- Number buttons -->
          <button 
            v-for="number in [1, 2, 3, 4, 5, 6, 7, 8, 9]" 
            :key="number"
            @click="appendDigit(number)"
            class="keypad-btn bg-white hover:bg-gray-50 border-2 border-gray-200 lg:text-lg lg:py-4"
          >
            {{ number }}
          </button>
        </div>
        
        <!-- Bottom row -->
        <div class="grid grid-cols-3 gap-3 mb-4">
          <!-- Decimal point (only for currency/decimal modes) -->
          <button 
            v-if="allowDecimal"
            @click="appendDecimal"
            :disabled="hasDecimal"
            class="keypad-btn bg-white hover:bg-gray-50 border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            .
          </button>
          <div v-else></div>
          
          <!-- Zero -->
          <button 
            @click="appendDigit(0)"
            class="keypad-btn bg-white hover:bg-gray-50 border-2 border-gray-200"
          >
            0
          </button>
          
          <!-- Backspace -->
          <button 
            @click="backspace"
            class="keypad-btn bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-600"
          >
            <svg class="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"/>
            </svg>
          </button>
        </div>

        <!-- Quick Amount Buttons (for currency mode) -->
        <div v-if="mode === 'currency' && showQuickAmounts" class="grid grid-cols-4 gap-2 mb-4">
          <button 
            v-for="amount in quickAmounts"
            :key="amount"
            @click="setQuickAmount(amount)"
            class="py-2 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium rounded-lg border border-blue-200 transition-colors"
          >
            ₹{{ amount }}
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-3">
          <button 
            @click="clear"
            class="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
          >
            Clear
          </button>
          <button 
            @click="confirm"
            :disabled="!canConfirm"
            class="flex-2 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {{ confirmText || 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  initialValue: {
    type: [Number, String],
    default: ''
  },
  mode: {
    type: String,
    default: 'number', // 'number', 'currency', 'decimal'
    validator: (value) => ['number', 'currency', 'decimal'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  confirmText: {
    type: String,
    default: ''
  },
  maxValue: {
    type: Number,
    default: null
  },
  minValue: {
    type: Number,
    default: 0
  },
  maxDigits: {
    type: Number,
    default: 10
  },
  showQuickAmounts: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['confirm', 'cancel', 'close'])

// State
const displayValue = ref('')

// Computed
const allowDecimal = computed(() => {
  return props.mode === 'currency' || props.mode === 'decimal'
})

const hasDecimal = computed(() => {
  return displayValue.value.includes('.')
})

const canConfirm = computed(() => {
  const value = parseFloat(displayValue.value) || 0
  const isValid = displayValue.value.length > 0
  const minValueCheck = props.minValue !== null ? value >= props.minValue : true
  const maxValueCheck = props.maxValue !== null ? value <= props.maxValue : true
  
  return isValid && minValueCheck && maxValueCheck
})

const quickAmounts = computed(() => {
  return [10, 20, 50, 100, 200, 500, 1000, 2000]
})

// Methods
const appendDigit = (digit) => {
  if (displayValue.value.length >= props.maxDigits) return
  
  // Prevent multiple leading zeros
  if (displayValue.value === '0' && digit === 0) return
  if (displayValue.value === '0' && digit !== 0) {
    displayValue.value = digit.toString()
    return
  }
  
  const newValue = displayValue.value + digit.toString()
  const numValue = parseFloat(newValue)
  
  // Check max value constraint
  if (props.maxValue !== null && numValue > props.maxValue) return
  
  displayValue.value = newValue
}

const appendDecimal = () => {
  if (!allowDecimal.value || hasDecimal.value) return
  if (displayValue.value === '') displayValue.value = '0'
  displayValue.value += '.'
}

const backspace = () => {
  if (displayValue.value.length > 0) {
    displayValue.value = displayValue.value.slice(0, -1)
  }
}

const clear = () => {
  displayValue.value = ''
}

const setQuickAmount = (amount) => {
  if (props.maxValue !== null && amount > props.maxValue) return
  displayValue.value = amount.toString()
}

const confirm = () => {
  const value = parseFloat(displayValue.value) || 0
  emit('confirm', value)
  close()
}

const close = () => {
  emit('close')
}

const handleBackdropClick = () => {
  emit('cancel')
  close()
}

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num)
}

// Watch for initial value changes
watch(() => props.initialValue, (newValue) => {
  if (newValue !== null && newValue !== undefined) {
    displayValue.value = newValue.toString()
  }
}, { immediate: true })

// Reset when modal opens
watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    nextTick(() => {
      if (props.initialValue !== null && props.initialValue !== undefined) {
        displayValue.value = props.initialValue.toString()
      } else {
        displayValue.value = ''
      }
    })
  }
})
</script>

<style scoped>
.keypad-btn {
  height: 3.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.2s;
  touch-action: manipulation;
  /* Prevent text selection on mobile */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  /* Prevent tap highlighting */
  -webkit-tap-highlight-color: transparent;
}

.keypad-btn:active {
  transform: scale(0.95);
}

.flex-2 {
  flex: 2;
}

/* Animation classes */
.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}

.animate-scale-in {
  animation: scaleIn 0.2s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9) translateY(-50px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(-50px);
    opacity: 1;
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .keypad-btn {
    height: 3rem;
    font-size: 1.125rem;
  }
}
</style>